/**
 * Registr časových řad pro grafy — ŽIJE MIMO ZUSTAND.
 *
 * Ring buffer je mutovatelný `Float32Array`; kdyby byl ve storu, jeho mutace by nikdy
 * nefire-ovala selektor, a kopírovat pole každý tik je přesně ten anti-vzor, kterému
 * se vyhýbáme. Komponenty se místo toho připojují přes `useSyncExternalStore` se
 * snapshotem typu `number` (sekvenční čítač) — primitiv, takže žádný tearing.
 *
 * Důsledek: nový vzorek v sérii `rpi|cpu.total` probudí JEDINÝ `<StripChart>`,
 * ne jeho rodiče a ne zbytek stránky.
 */

import { clearRing, createRing, push, type Ring } from "@client/lib/sysmon/ring";
import type { SysmonSnapshot } from "@globals/sysmon/types";

/** 3 minuty při 1 Hz (btop okno), 6 minut při 2 s. */
export const SERIES_CAPACITY = 180;
/** Mini graf v řádku procesu má ~60 px — víc bodů nemá rozlišení. */
export const PROC_SERIES_CAPACITY = 32;
/** Tvrdé stropy, aby paměť nerostla s dobou běhu stránky. */
export const MAX_SERIES = 128;
export const MAX_PROC_SERIES = 96;

export type SeriesKey = string;

interface Entry {
  ring: Ring;
  seq: number;
  listeners: Set<() => void>;
  lastTouchedMs: number;
  proc: boolean;
}

const series = new Map<SeriesKey, Entry>();
/** Poslední `ts` snapshotu per hostitel — z něj se pozná díra v datech. */
const lastTsByHost = new Map<string, number>();

const now = (): number => Date.now();

const evictIfNeeded = (proc: boolean): void => {
  const limit = proc ? MAX_PROC_SERIES : MAX_SERIES;
  let count = 0;
  for (const e of series.values()) if (e.proc === proc) count += 1;
  if (count <= limit) return;

  // LRU: obětuje se nejdéle nepsaná série.
  let oldestKey: SeriesKey | null = null;
  let oldestAt = Number.POSITIVE_INFINITY;
  for (const [key, e] of series) {
    if (e.proc !== proc) continue;
    if (e.lastTouchedMs < oldestAt) {
      oldestAt = e.lastTouchedMs;
      oldestKey = key;
    }
  }
  if (oldestKey) series.delete(oldestKey);
};

const ensure = (key: SeriesKey, proc: boolean): Entry => {
  let entry = series.get(key);
  if (!entry) {
    entry = {
      ring: createRing(proc ? PROC_SERIES_CAPACITY : SERIES_CAPACITY),
      seq: 0,
      listeners: new Set(),
      lastTouchedMs: now(),
      proc,
    };
    series.set(key, entry);
    evictIfNeeded(proc);
  }
  return entry;
};

const notify = (entry: Entry): void => {
  entry.seq += 1;
  entry.lastTouchedMs = now();
  for (const l of entry.listeners) l();
};

export const pushSample = (key: SeriesKey, value: number, proc = false): void => {
  const entry = ensure(key, proc);
  push(entry.ring, value);
  notify(entry);
};

export const readSeries = (key: SeriesKey): Ring | undefined => series.get(key)?.ring;

/** Snapshot pro `useSyncExternalStore` — musí být PRIMITIV a stabilní mezi změnami. */
export const seriesSeq = (key: SeriesKey): number => series.get(key)?.seq ?? 0;

export const subscribeSeries = (key: SeriesKey, cb: () => void): (() => void) => {
  const entry = ensure(key, key.includes("|proc."));
  entry.listeners.add(cb);
  return () => {
    entry.listeners.delete(cb);
  };
};

// ── Rozstříhání snapshotu na série ───────────────────────────────────────────

/** Klíč vždy začíná hostitelem — přepnutí hosta tak nikdy nesmíchá data. */
export const seriesKey = (hostId: string, name: string): SeriesKey => `${hostId}|${name}`;

const pushIf = (hostId: string, name: string, value: number | null | undefined): void => {
  if (value === null || value === undefined) return;
  pushSample(seriesKey(hostId, name), value);
};

/**
 * Mezera delší než 2,5× interval znamená pauzu, reconnect nebo výpadek hostitele.
 * Vložíme jeden `NaN`, aby graf ukázal skutečnou díru místo lživé přímky.
 */
const insertGapIfNeeded = (snapshot: SysmonSnapshot, keys: string[]): void => {
  const prevTs = lastTsByHost.get(snapshot.hostId);
  lastTsByHost.set(snapshot.hostId, snapshot.ts);
  if (prevTs === undefined) return;
  const gap = snapshot.ts - prevTs;
  if (gap <= snapshot.intervalMs * 2.5) return;
  for (const name of keys) pushSample(seriesKey(snapshot.hostId, name), Number.NaN);
};

const seriesNamesFor = (snapshot: SysmonSnapshot): string[] => {
  const names = ["cpu.total", "mem.used", "swap.used", "cpu.temp"];
  // Jen u hostitele, který ty řady vůbec plní. Jinak by se díra zapsala do série, do které
  // se pak nikdy nic nepíše — a ta by zabírala místo v LRU stropu `MAX_SERIES`.
  if (snapshot.app !== null) names.push("app.rss", "app.lag");
  if (snapshot.routeros !== null) names.push("wifi.clients");
  for (const c of snapshot.cpu.cores) names.push(`cpu.core.${c.index}`);
  for (const n of snapshot.net) names.push(`net.rx.${n.name}`, `net.tx.${n.name}`);
  for (const d of snapshot.disks) names.push(`disk.read.${d.mountpoint}`, `disk.write.${d.mountpoint}`);
  return names;
};

export const pushSnapshot = (snapshot: SysmonSnapshot): void => {
  const host = snapshot.hostId;
  insertGapIfNeeded(snapshot, seriesNamesFor(snapshot));

  pushIf(host, "cpu.total", snapshot.cpu.totalPercent);
  pushIf(host, "cpu.temp", snapshot.cpu.tempC);
  for (const core of snapshot.cpu.cores) pushIf(host, `cpu.core.${core.index}`, core.usagePercent);

  pushIf(host, "mem.used", snapshot.mem.usedPercent);
  pushIf(host, "swap.used", snapshot.mem.swap?.usedPercent ?? null);

  for (const iface of snapshot.net) {
    pushIf(host, `net.rx.${iface.name}`, iface.rxBps);
    pushIf(host, `net.tx.${iface.name}`, iface.txBps);
  }

  for (const disk of snapshot.disks) {
    pushIf(host, `disk.read.${disk.mountpoint}`, disk.readBps);
    pushIf(host, `disk.write.${disk.mountpoint}`, disk.writeBps);
  }

  // `app` je null u hostitele, který není instancí Demirelu (MikroTik AP).
  if (snapshot.app !== null) {
    pushIf(host, "app.rss", snapshot.app.rssB);
    pushIf(host, "app.lag", snapshot.app.eventLoopLagP99Ms);
  }

  // Počet asociovaných klientů v čase. Jediná řada, která u AP dává smysl navíc — na ní je
  // vidět flapping (klient se odpojuje a připojuje), což z okamžitého čísla poznat nejde.
  if (snapshot.routeros !== null) {
    pushIf(host, "wifi.clients", snapshot.routeros.clients.length);
  }
};

export const pushProcSamples = (hostId: string, rows: { pid: number; cpuPercent: number }[]): void => {
  for (const row of rows) pushSample(seriesKey(hostId, `proc.${row.pid}`), row.cpuPercent, true);
};

/** Úklid po hostiteli, který dlouho nedodal data (vypnutý VPS). */
export const pruneHost = (hostId: string): void => {
  const prefix = `${hostId}|`;
  for (const key of series.keys()) if (key.startsWith(prefix)) series.delete(key);
  lastTsByHost.delete(hostId);
};

export const clearHistory = (): void => {
  for (const entry of series.values()) clearRing(entry.ring);
  series.clear();
  lastTsByHost.clear();
};
